import React, { useEffect, useState } from "react";
import { adminFetchBtwMailContext } from "../core/axios/endpoints";

const fmtEUR = (n, digits = 2) =>
  n === null || n === undefined
    ? "-"
    : `€ ${Number(n).toLocaleString("nl-NL", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      })}`;

// "2026-09-01" -> "1-9-2026" zonder Date-object, dus geen UTC-verschuiving.
const fmtIsoDatum = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso ?? "");
  return m ? `${Number(m[3])}-${Number(m[2])}-${m[1]}` : "-";
};

const Row = ({ label, value, strong = false }) => (
  <div className="grid grid-cols-3 gap-4 border-b border-line py-2 text-sm last:border-b-0">
    <dt className="col-span-1 text-ink-subtlest">{label}</dt>
    <dd
      className={`col-span-2 tabular-nums ${
        strong ? "font-semibold text-ink" : "text-ink"
      }`}
    >
      {value ?? "-"}
    </dd>
  </div>
);

// Berekening van de verwachte teruggave, zoals de server die maakt voor de
// klantmails ({{btwFactuur}}, {{afdrachtEersteJaar}}, {{nettoTeruggave}}).
// Dit blok laat zien welke formule we hanteren, zodat een admin weet wat er
// in een mail komt te staan. De berekening zelf staat op de server in
// utils/btwRefundCalc.ts; hier wordt niets herberekend.
const BtwBerekeningSection = ({ applicationId, reloadKey }) => {
  const [ctx, setCtx] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!applicationId) return undefined;
    let cancelled = false;
    adminFetchBtwMailContext(applicationId)
      .then((res) => {
        if (!cancelled) setCtx(res?.data ?? res);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e?.response?.data?.error ?? "Berekening laden mislukt.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [applicationId, reloadKey]);

  const calc = ctx?.calc;

  return (
    <section className="mb-5 rounded bg-white p-5 shadow-card">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-base font-semibold text-ink">
          Berekening teruggave
        </h3>
        {calc?.compleet ? (
          <span className="inline-flex items-center rounded bg-[#DCFFF1] px-1.5 py-px text-[11px] font-bold uppercase tracking-wide text-[#1F845A]">
            Verwacht {fmtEUR(calc.nettoTeruggave)}
          </span>
        ) : null}
      </div>

      {error ? (
        <p className="rounded bg-[#FFECEB] px-3 py-2 text-sm text-[#AE2A19]">{error}</p>
      ) : !calc ? (
        <p className="text-sm text-ink-subtlest">Laden…</p>
      ) : (
        <>
          <dl>
            <Row label="Aankoopbedrag (incl. btw)" value={fmtEUR(calc.aankoopbedragIncl)} />
            <Row label="BTW op de factuur" value={fmtEUR(calc.btwFactuur)} />
            <Row
              label="Capaciteit batterij"
              value={
                calc.capaciteitKwh !== null
                  ? `${Number(calc.capaciteitKwh).toLocaleString("nl-NL")} kWh`
                  : "-"
              }
            />
            <Row
              label="Afdracht per jaar"
              value={
                calc.afdrachtPerJaar !== null
                  ? `${fmtEUR(calc.afdrachtPerJaar)} (${Number(
                      calc.capaciteitKwh,
                    ).toLocaleString("nl-NL")} kWh x ${fmtEUR(calc.afdrachtPerKwh, 0)})`
                  : "-"
              }
            />
            <Row
              label="Installatiedatum"
              value={fmtIsoDatum(calc.installatiedatum)}
            />
            {calc.startdatumBron === "ingangsdatum_dynamisch_contract" ? (
              <Row
                label="Startdatum afdracht"
                value={`${fmtIsoDatum(calc.startdatumAfdracht)} (ingangsdatum dynamisch contract, later dan installatie)`}
              />
            ) : null}
            <Row
              label="Afdracht eerste jaar"
              value={
                calc.eersteJaar
                  ? `${fmtEUR(calc.afdrachtEersteJaar)} (${calc.eersteJaar.dagenActief} van ${calc.eersteJaar.dagenInJaar} dagen in ${calc.eersteJaar.jaar})`
                  : "-"
              }
            />
            <Row
              label="Netto teruggave"
              value={fmtEUR(calc.nettoTeruggave)}
              strong
            />
          </dl>

          {calc.warnings?.length > 0 ? (
            <div className="mt-3 rounded bg-[#FFF7D6] px-3 py-2 text-sm text-[#7F5F01]">
              <p className="font-semibold">Berekening niet compleet</p>
              <ul className="mt-1 list-disc pl-4">
                {calc.warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
              <p className="mt-1">
                Mails met bedragen kunnen pas verstuurd worden als dit is
                aangevuld bij &quot;Gegevens voor BTW-teruggave&quot;.
              </p>
            </div>
          ) : null}

          <details className="mt-3 text-xs text-ink-subtlest">
            <summary className="cursor-pointer select-none text-brand hover:underline">
              Welke formule hanteren we?
            </summary>
            <ul className="mt-2 list-disc space-y-0.5 pl-4">
              {(calc.formule ?? []).map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
            <p className="mt-2">
              De afdracht is de BTW over de opbrengst van de verhandelde
              energie, vooraf voorzichtig geschat. De batterij handelt vanaf
              de installatiedatum, of vanaf de ingangsdatum van het dynamisch
              energiecontract als die later is, dus het eerste jaar gaat naar
              rato. Deze
              bedragen komen als variabelen in de klantmails.
            </p>
          </details>
        </>
      )}
    </section>
  );
};

export default BtwBerekeningSection;
